// ---------------------------------------------------------
//  Hatim Screen -- Quran Hatim (30 Juz) tracking grid
// ---------------------------------------------------------

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import {
  ArrowLeft, BookOpen, Check, Clock,
} from "lucide-react";

interface JuzItem {
  juzNumber: number;
  userId: string | null;
  userName: string | null;
  status: string; // "unassigned" | "pending" | "completed"
}

interface HatimScreenProps {
  groupId: string;
  groupName: string;
  juzList: JuzItem[];
  onAssign: (juzNumber: number) => void;
  onComplete: (assignmentId: string) => void;
  onBack: () => void;
  userId: string;
}

function getStatusColor(status: string, isMe: boolean): string {
  switch (status) {
    case "completed":
      return "border-ehille-green bg-ehille-green/10";
    case "pending":
      return isMe
        ? "border-gold bg-gold/15 ring-1 ring-gold/30"
        : "border-gold bg-gold/10";
    default:
      return "border-border bg-muted/50 hover:border-gold/40 hover:bg-gold/5";
  }
}

export function HatimScreen({
  groupId: _groupId, groupName, juzList, onAssign, onComplete, onBack, userId,
}: HatimScreenProps) {
  const completedCount = juzList.filter(j => j.status === "completed").length;
  const assignedCount = juzList.filter(j => j.status === "pending").length;
  const remainingCount = juzList.filter(j => j.status === "unassigned").length;
  const progressPct = Math.round((completedCount / 30) * 100);

  const handleCellClick = (juz: JuzItem) => {
    if (juz.status === "unassigned") {
      onAssign(juz.juzNumber);
    }
  };

  return (
    <div className="pb-24 px-4 pt-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          aria-label={t("back")}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">Hatim</h1>
          <p className="text-xs text-muted-foreground truncate">{groupName}</p>
        </div>
        <div className="bg-gold/10 rounded-full px-3 py-1.5 shrink-0">
          <span className="text-sm font-bold text-gold">{completedCount}/30</span>
        </div>
      </div>

      {/* Progress card */}
      <Card className="mb-6 border-gold/20 bg-gradient-to-br from-ehille-dark to-ehille-dark2 overflow-hidden relative">
        <div className="absolute top-2 right-3 text-4xl opacity-10 select-none">
          <BookOpen size={48} />
        </div>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-white">Hatim Ilerlemesi</h3>
            <span className="text-sm font-bold text-gold">{progressPct}%</span>
          </div>
          <Progress value={progressPct} className="h-3 mb-4" />

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <div className="w-2 h-2 rounded-full bg-ehille-green" />
                <span className="text-lg font-bold text-ehille-green">{completedCount}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Tamamlanan</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <div className="w-2 h-2 rounded-full bg-gold" />
                <span className="text-lg font-bold text-gold">{assignedCount}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Atanmis</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                <span className="text-lg font-bold text-muted-foreground">{remainingCount}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Kalan</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-muted border border-border" />
          <span className="text-[10px] text-muted-foreground">Bos</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-gold/30 border border-gold" />
          <span className="text-[10px] text-muted-foreground">Atandi</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-ehille-green/30 border border-ehille-green" />
          <span className="text-[10px] text-muted-foreground">Bitti</span>
        </div>
      </div>

      {/* 5x6 Juz Grid */}
      <div className="grid grid-cols-5 gap-2 mb-6">
        {juzList.map((juz) => {
          const isMe = juz.userId === userId;
          const isUnassigned = juz.status === "unassigned";
          const isCompleted = juz.status === "completed";
          const isPending = juz.status === "pending";

          return (
            <button
              key={juz.juzNumber}
              onClick={() => handleCellClick(juz)}
              disabled={!isUnassigned && !(isPending && isMe)}
              className={cn(
                "relative flex flex-col items-center justify-center rounded-2xl border-2 p-2 min-h-[80px] transition-all",
                getStatusColor(juz.status, isMe),
                isUnassigned && "cursor-pointer active:scale-[0.95]",
                isPending && isMe && "cursor-default",
                (isCompleted || (isPending && !isMe)) && "cursor-default",
              )}
              aria-label={`Cuz ${juz.juzNumber} - ${juz.status === "unassigned" ? "Bos" : juz.userName ?? ""}`}
            >
              {/* Juz number */}
              <span className={cn(
                "text-lg font-bold leading-none",
                isCompleted && "text-ehille-green",
                isPending && "text-gold",
                isUnassigned && "text-muted-foreground",
              )}>
                {juz.juzNumber}
              </span>

              {/* Status indicator */}
              {isCompleted && (
                <div className="w-5 h-5 rounded-full bg-ehille-green flex items-center justify-center mt-1">
                  <Check size={12} className="text-white" strokeWidth={3} />
                </div>
              )}

              {isPending && (
                <div className="flex items-center gap-0.5 mt-1">
                  <Clock size={9} className="text-gold" />
                  <span className="text-[8px] font-semibold text-gold truncate max-w-[48px]">
                    {isMe ? "Sen" : juz.userName ?? "?"}
                  </span>
                </div>
              )}

              {isUnassigned && (
                <span className="text-[9px] text-muted-foreground mt-1">Bos</span>
              )}

              {/* "Sen" badge for own assignments */}
              {isMe && isPending && (
                <Badge variant="warning" className="absolute -top-1.5 -right-1.5 text-[7px] h-3.5 px-1">
                  Sen
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* My assigned juz -- completion section */}
      {juzList.filter(j => j.userId === userId && j.status === "pending").length > 0 && (
        <Card className="mb-4 border-gold/30">
          <CardContent className="p-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
              Sana Atanan Cuzler
            </h3>
            <div className="space-y-2">
              {juzList
                .filter(j => j.userId === userId && j.status === "pending")
                .map((juz) => (
                  <div key={juz.juzNumber} className="flex items-center gap-3 p-2.5 rounded-xl bg-gold/5">
                    <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-gold">{juz.juzNumber}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">Cuz {juz.juzNumber}</p>
                      <p className="text-[10px] text-muted-foreground">Okumaya devam edin</p>
                    </div>
                    <Button
                      size="sm"
                      className="h-8 px-3 bg-ehille-green hover:bg-ehille-green/90 text-white text-xs"
                      onClick={() => onComplete(String(juz.juzNumber))}
                    >
                      <Check size={14} className="mr-1" />
                      Tamamla
                    </Button>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recently completed */}
      {juzList.filter(j => j.status === "completed").length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
              Tamamlanan Cuzler
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {juzList
                .filter(j => j.status === "completed")
                .map((juz) => (
                  <div
                    key={juz.juzNumber}
                    className="flex items-center gap-1.5 bg-ehille-green/10 rounded-full px-2.5 py-1"
                  >
                    <Check size={10} className="text-ehille-green" />
                    <span className="text-[10px] font-semibold text-ehille-green">
                      Cuz {juz.juzNumber}
                    </span>
                    {juz.userName && (
                      <span className="text-[9px] text-ehille-green/60">- {juz.userName}</span>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
